// @flow
import React, { Component } from 'react';
import styled from 'styled-components';

import type { TRound } from '../../../modules/types';
import type { TCoachPlayerStatsById  } from '../../../modules/types';

import { getTeamProjectedScore } from '../../../helpers/team';

import BorderedIcon from "../../../components/BorderedIcon";
import ArrowRightIcon from '../../../components/Icons/ArrowRight';
import CountdownTimer from '../../../components/CountdownTimer';
import Fixture from '../../../components/Fixture';
import colors from '../../../assets/css/colors';
import { above } from '../../../assets/css/media';
import { getTeamScore } from '../../../helpers/complex/team';
import LeagueBase from './LeagueBase';

import { CountdownTimerWrapper, CountdownLabel } from './countdownTimerStyles';
import LeagueStatusLabel from './LeagueStatusLabel';
import IconLinkStyled from './IconLinkStyled';
import getLinkStates from './getLinkStates';

const LeagueStatusLabelStyled = styled(LeagueStatusLabel)`
	text-align: center;
	margin-right: 40px;
`;

const ByeLeagueStatusLabelStyled = styled(LeagueStatusLabel)`
	text-align: center;
	margin-right: 20px;
`;

const LeagueByeLabel = styled.div`
	text-transform: uppercase;
	white-space: nowrap;
	font-size: 14px;
	font-weight: bold;
	line-height: 1.57;
	color: #ffffff;
	background: ${colors.icon_grey};
	border-radius: 2px;
	padding: 0 10px;
	margin: 5px 15px;

	&:after {
		content: '';

		${above.tablet`
			content: ' Round';
		`}
	}
`;

type TLeague = Object;

type Props = {
	league: TLeague,
	onTimerComplete: Function,
	is_commissioner: boolean,
	round: TRound,
	fixture_round: TRound,
	user: {
		id: number,
		avatar_version: number,
		firstname: string,
		lastname: string
	},
	coach_players_by_id: TCoachPlayerStatsById,
	has_assistant_coach: boolean,
	players: Object
};

const isByeRoundForLeague = (league: TLeague, round: TRound) => {
	const league_plays_bye_rounds = Boolean(league.bye_rounds);
	const is_bye_round = Boolean(round.is_bye);

	return is_bye_round && !league_plays_bye_rounds;
};

class LiveLeague extends Component<Props> {
	onTimerComplete = (date_string: string) => {
		const { onTimerComplete } = this.props;
		onTimerComplete(date_string);
	}

	renderRightSide = () => {
		const { league, round, is_commissioner } = this.props;
		const links = getLinkStates(league, is_commissioner);

		const round_is_scheduled = round.status === 'scheduled';
		const date = round.start;
		const round_is_active = round.status === 'active';

		const show_bye_round_message = isByeRoundForLeague(league, round);

		return (
			<React.Fragment>
				{show_bye_round_message && (
					<ByeLeagueStatusLabelStyled>
						No fixtures in Round {round.id} due to league bye settings
					</ByeLeagueStatusLabelStyled>
				)}
				{round_is_active && !show_bye_round_message && (
					<LeagueStatusLabelStyled>Round in progress</LeagueStatusLabelStyled>
				)}
				{round_is_scheduled && !show_bye_round_message && (
					<CountdownTimerWrapper>
						<CountdownLabel>
							Round {round.id} Lockout
						</CountdownLabel>
						<CountdownTimer
							key={date}
							is_uppercase
							onComplete={this.onTimerComplete.bind(this, date)}
							date={date}
						/>
					</CountdownTimerWrapper>
				)}

				{show_bye_round_message && (<LeagueByeLabel>Bye</LeagueByeLabel>)}

				<IconLinkStyled to={links.arrow} title="Go to League">
					<BorderedIcon hover={colors.lightGreen}>
						<ArrowRightIcon
							cursor="pointer"
							size="1.875"
							color={colors.nrlDarkGreen}
						/>
					</BorderedIcon>
				</IconLinkStyled>
			</React.Fragment>
		);
	}

	renderBelow = () => {
		const {
			league,
			user,
			fixture_round,
			coach_players_by_id,
			has_assistant_coach,
			players
		} = this.props;

		const is_bye_round_for_league = isByeRoundForLeague(league, fixture_round);

		if (league && league.opponent && !is_bye_round_for_league) {
			const opponent = league.opponent;
			const home_lineup = league.my_lineup;
			const away_lineup = opponent.lineup;

			const home_team_live_score = getTeamScore({
				lineup:home_lineup,
				players_by_id: players,
				fixture_round
			});

			const away_team_live_score = getTeamScore({
				lineup: away_lineup,
				players_by_id: players,
				fixture_round
			});

			const home_lineup_project_score =
				getTeamProjectedScore(
					home_lineup, players, coach_players_by_id, league, fixture_round
				);
			const away_lineup_project_score =
				getTeamProjectedScore(
					away_lineup, players, coach_players_by_id, league, fixture_round
				);

			const show_previous_score = fixture_round.status !== 'active';

			const fixture = {
				home: {
					id: league.team_id,
					name: league.team_name,
					firstname: user.firstname,
					lastname: user.lastname,
					points: show_previous_score? Number(league.team_points)
						: Number(home_team_live_score),
					projected_points: home_lineup_project_score,
					lineup: league.my_lineup,
					rank: league.rank,
					user_id: user.id,
					avatar_version: user.avatar_version
				},
				away: {
					id: opponent.team_id,
					name: opponent.team_name,
					firstname: opponent.firstname,
					lastname: opponent.lastname,
					points: show_previous_score? Number(opponent.score)
						: Number(away_team_live_score),
					projected_points: away_lineup_project_score,
					lineup: opponent.lineup,
					rank: opponent.rank,
					user_id: opponent.user_id,
					avatar_version: opponent.avatar_version
				}
			};

			return (
				<Fixture
					fixture={fixture}
					league={league}
					round={fixture_round}
					has_assistant_coach={has_assistant_coach}
					players={players}
				/>
			);
		}

		return null;
	}

	render() {
		const { league, is_commissioner } = this.props;

		return (
			<LeagueBase
				league={league}
				is_commissioner={is_commissioner}
				renderRightSide={this.renderRightSide}
				renderBelow={this.renderBelow}
			/>
		);
	}
}

export default LiveLeague;


// WEBPACK FOOTER //
// ./src/pages/leagues/myLeagues/LiveLeague.js